import React, {
    useEffect, useContext, useCallback, useState
} from 'react';
import _ from 'lodash';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { VehicleInfoLookupService } from 'gw-capability-vehicleinfo';
import { TranslatorContext } from '@jutro/locale';
import { useValidation } from 'gw-portals-validation-react';
import { ADOBE_TOPICS, SgVehicleLookupUtil } from 'saga-modules';
import SpmConstants from 'sg-spm-constants';
import { publish } from '@jutro/events';
import SPM_TAGGING_PROPERTIES from 'sg-adobe-analytics/SPM/SPMTaggingProperties';
import metadata from './SgRegistrationLookup.metadata.json5';
import messages from './SgRegistrationLookup.messages';

function SpmRegistrationLookup(props) {
    const translator = useContext(TranslatorContext);
    const {
        id,
        modelVM,
        vehicleModel,
        updateData,
        changeVehicleView,
        showErrors,
        setShowErrors,
        messageParams,
        checkManualLookups,
        isCarInsurance,
        handleVehicleValue,
        allowLookupOnly
    } = props;
    const {
        onValidate
    } = useValidation(id);
    const [landingPageValues, setLandingPageValues] = useState({ registration: vehicleModel.registration });
    const [loading, setLoading] = useState(false);
    const [lookupResult, setLookupResult] = useState('');

    // don't know reg link
    const toManualLookup = useCallback(() => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, {
            adobeID: SPM_TAGGING_PROPERTIES.doYouKnowVehicleRegistration,
            value: false
        });
        if (!_.isEmpty(vehicleModel.registrationNumber.value)
            && (lookupResult === SpmConstants.VEHICLE_LOOKUP_STATUS.NOT_FOUND || lookupResult === SpmConstants.VEHICLE_LOOKUP_STATUS.ERROR)) {
            vehicleModel.isRegKnown.value = true;
        } else {
            vehicleModel.isRegKnown.value = false;
            SgVehicleLookupUtil.clearVehicleProperties(vehicleModel, ['registrationNumber']);
        }

        updateData(_.clone(modelVM));
        changeVehicleView(SpmConstants.VEHICLE_LOOKUP_VIEWS.MANUAL_LOOKUP);
    }, [changeVehicleView, lookupResult, modelVM, updateData, vehicleModel]);

    const publishLookupStatus = (successState) => {
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: SPM_TAGGING_PROPERTIES.regLookupStatus, value: successState });
    };

    const handleInvalidReg = useCallback(() => {
        setShowErrors(true);
        if (vehicleModel.registrationNumber.aspects.valid !== true) {
            publishLookupStatus(SpmConstants.VEHICLE_LOOKUP_STATUS.INVALID_REG);
        }
    }, [setShowErrors, vehicleModel]);

    const updateLookupData = useCallback((vehicleInfo) => {
        vehicleModel.value = _.merge(
            { ...vehicleModel.value },
            vehicleInfo,
        );
        vehicleModel.isRegKnown.value = true;
        handleVehicleValue(vehicleInfo);
        updateData(_.clone(modelVM));
    }, [handleVehicleValue, modelVM, updateData, vehicleModel]);

    const isCarLookupValid = (data) => {
        return !_.isNil(data.make) && !_.isNil(data.model);
    };

    const isMotorHomeLookupValid = (data) => {
        return !_.isNil(data.engineCapacity);
    };

    const isLookupValid = (data) => {
        if (_.isEmpty(data)) {
            return false;
        }
        if (!isCarInsurance) {
            return isMotorHomeLookupValid(data);
        }
        return isCarLookupValid(data);
    };

    const lookupReg = useCallback(() => {
        setShowErrors(false);
        if (vehicleModel.registrationNumber.aspects.valid !== true || vehicleModel.registrationNumber.value === undefined) {
            handleInvalidReg();
            return;
        }
        setLoading(true);
        VehicleInfoLookupService.lookupVehicleInfoBasedOnRegistration(
            vehicleModel.registrationNumber.value, isCarInsurance
        ).then((vehicleInfo) => {
            if (!isLookupValid(vehicleInfo) || vehicleInfo.successState !== SpmConstants.VEHICLE_LOOKUP_STATUS.SUCCESS) {
                // set notFound value to true for error validation messages
                setLookupResult(SpmConstants.VEHICLE_LOOKUP_STATUS.NOT_FOUND);
                setShowErrors(true);
                vehicleModel.isRegKnown.value = true;
            } else {
                updateLookupData(vehicleInfo);
                if (isCarInsurance) {
                    changeVehicleView(SpmConstants.VEHICLE_LOOKUP_VIEWS.VEHICLE_SUMMARY);
                } else {
                    vehicleModel.bodyType.value = SpmConstants.WEB_MOTOR_HOME_BODY_TYPE; // Body type must be manually set
                    changeVehicleView(SpmConstants.VEHICLE_LOOKUP_VIEWS.MANUAL_LOOKUP);
                }
            }
            publishLookupStatus(vehicleInfo.successState);
            setLoading(false);
        }).catch(() => {
            // set lookupError value to true for error validation messages
            setLookupResult(SpmConstants.VEHICLE_LOOKUP_STATUS.ERROR);
            vehicleModel.isRegKnown.value = true;
            publishLookupStatus(SpmConstants.VEHICLE_LOOKUP_STATUS.FAILURE);
            // check for manual lookups on fail to see if should go on to error page - PC not available
            checkManualLookups();
        });
    }, [changeVehicleView, checkManualLookups, handleInvalidReg, isCarInsurance,
        setShowErrors, updateLookupData, vehicleModel]);

    const setRegToUpperCase = (value, path) => {
        const newModelVM = _.clone(modelVM);
        if (value === '') {
            _.set(newModelVM, path, undefined);
        } else {
            _.set(newModelVM, path, value.replace(' ', '').toUpperCase());
        }
        updateData(newModelVM);
    };

    const getValidationMessage = () => {
        if (_.isEmpty(vehicleModel.registrationNumber.value)) {
            return [translator({ id: 'displaykey.Edgev11.Web.Api.Model.RegistrationRequired', defaultMessage: '' })];
        } if (vehicleModel.registrationNumber.aspects.valid !== true) {
            return vehicleModel.registrationNumber.aspects.validationMessages;
        } if (lookupResult === SpmConstants.VEHICLE_LOOKUP_STATUS.NOT_FOUND || lookupResult === SpmConstants.VEHICLE_LOOKUP_STATUS.ERROR) {
            return [allowLookupOnly ? translator(messages.regNotFoundErrorLookupOnly) : translator(messages.regNotFoundError)];
        } if (showErrors && _.isNil(vehicleModel.description.value)) {
            return [translator(messages.lookupIncomplete, messageParams)];
        }
        return undefined;
    };

    // Lookup address from landing page data if present
    useEffect(() => {
        if (landingPageValues && landingPageValues.registration) {
            vehicleModel.registrationNumber.value = landingPageValues.registration;
            setLandingPageValues(undefined);
            lookupReg();
        }
    }, [landingPageValues]);

    const overrideProps = {
        registrationLookupActions: {
            visible: !loading ? '' : false
        },
        Registration: {
            label: translator(messages.registrationLabel, messageParams),
            onValueChange: setRegToUpperCase,
            validationMessages: getValidationMessage(),
        },
        vehicleRegistrationFindCar: {
            content: translator(messages.findVehicleButton, messageParams)
        },
        enterManualDetails: {
            visible: !allowLookupOnly ? '' : false,
            content: translator(messages.enterManualDetails)
        },
        loader: {
            visible: loading ? '' : false
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            lookupReg,
            toManualLookup
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            onModelChange={updateData}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            showErrors={showErrors}
            onValidationChange={onValidate}
        />
    );
}

export default SpmRegistrationLookup;
